import { createContext, useContext, useState, ReactNode, useEffect } from "react";

type Language = "it" | "en" | "es";
const SUPPORTED: Language[] = ["it", "en", "es"];

/**
 * Detect the visitor's preferred language from the browser/system.
 * Runs on every page load — including when someone opens a link shared
 * by another user — so the UI adapts to whoever is looking at it right
 * now, not to whoever created or shared the link.
 * Falls back to Italian when the system language isn't supported.
 */
function detectSystemLanguage(): Language {
  if (typeof navigator === "undefined") return "it";
  const candidates: string[] = [];
  if (Array.isArray(navigator.languages)) candidates.push(...navigator.languages);
  if (navigator.language) candidates.push(navigator.language);
  for (const raw of candidates) {
    const code = raw.toLowerCase().split(/[-_]/)[0] as Language;
    if (SUPPORTED.includes(code)) return code;
  }
  return "it";
}

interface LanguageContextType {
  lang: Language;
  /** Kept for backward compatibility; language is auto-detected and not user-selectable. */
  setLang: (lang: Language) => void;
  t: {
    (it: string, en: string, es?: string): string;
    (it: string[], en: string[], es?: string[]): string[];
  };
}

const LanguageContext = createContext<LanguageContextType>({
  lang: "it",
  setLang: () => {},
  t: ((it: any, _en: any) => it) as any,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Language>(() => detectSystemLanguage());

  // Re-detect if the browser/system language changes while the tab is open.
  useEffect(() => {
    const onLangChange = () => setLang(detectSystemLanguage());
    window.addEventListener("languagechange", onLangChange);
    return () => window.removeEventListener("languagechange", onLangChange);
  }, []);

  // Backward compatible: legacy calls `t(it, en)` still work — Spanish
  // visitors fall back to English until an `es` translation is provided.
  const t = ((it: any, en: any, es?: any) => {
    if (lang === "it") return it;
    if (lang === "es") return es ?? en;
    return en;
  }) as LanguageContextType["t"];

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
